import React, {
  FC,
  useCallback,
  useState,
  useMemo,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
} from 'react';
import debounce from 'lodash/debounce';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { N700 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';

import * as stylesGlobal from '@/styles';

import { testIds } from '../constants';

import * as styles from './styles';

type SearchBoxProps = {
  inputValue: string;
  isLoading: boolean;
  onChange: (value: string) => void; // This is already a debounced change callback
};

const SearchBox: FC<SearchBoxProps> = ({ inputValue = '', onChange, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const debounceChangeFn = useCallback(debounce(onChange, 500), []);
  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
    debounceChangeFn(value);
  };

  const handleInputClear = () => {
    setSearchTerm('');
    onChange('');
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && event.currentTarget) event.currentTarget.blur();
  };

  const clearInputElement = useMemo(
    () =>
      searchTerm ? (
        <div onClick={handleInputClear} css={stylesGlobal.cursorPointer}>
          <CrossIcon
            primaryColor={token('color.text.subtlest', N700)}
            size="small"
            label=""
            testId={testIds.searchBoxInputClear}
          />
        </div>
      ) : (
        <></>
      ),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [searchTerm]
  );

  const elementBeforeInput = (
    <SearchIcon primaryColor={token('color.text.subtlest', N700)} size="small" label="" />
  );
  const elementAfterInput = (
    <span css={styles.searchFieldStatusStyles}>
      {searchTerm && isLoading ? (
        <Spinner size="small" testId={testIds.searchBoxInputSpinner} />
      ) : (
        clearInputElement
      )}
    </span>
  );

  useEffect(() => {
    setSearchTerm(inputValue);
  }, [inputValue]);

  return (
    <Textfield
      value={searchTerm}
      onChange={handleTextFieldChange}
      onKeyPress={handleKeyPress}
      isMonospaced={false}
      css={styles.searchFieldStyles}
      className="search-view-text-field"
      placeholder="Search by name of SLO Group, SLO, Capability, Team or Product"
      elemBeforeInput={elementBeforeInput}
      elemAfterInput={elementAfterInput}
      testId={testIds.searchBoxInput}
    />
  );
};

export default SearchBox;
